import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { MessageSquarePlus, Bug, Lightbulb, ImageIcon, StickyNote, Reply } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  new: { label: 'New', cls: 'bg-orange-100 text-orange-800' },
  in_progress: { label: 'In Progress', cls: 'bg-blue-100 text-blue-800' },
  roadmapped: { label: 'Roadmapped', cls: 'bg-purple-100 text-purple-800' },
  resolved: { label: 'Resolved', cls: 'bg-green-100 text-green-800' },
  declined: { label: 'Declined', cls: 'bg-slate-200 text-slate-600' },
};

const mediaSrc = (url) => (!url ? url : (url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`));

export const FeedbackAdminPanel = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');
  const [noteEditing, setNoteEditing] = useState({}); // id -> draft note
  const [replyEditing, setReplyEditing] = useState({}); // id -> draft reply
  const [replySending, setReplySending] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/feedback`);
      setItems(res.data || []);
    } catch (e) {
      // panel hides errors quietly; admins will see empty state
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const updateStatus = async (item, status, note) => {
    try {
      const payload = { status };
      if (note !== undefined) payload.admin_note = note;
      const res = await axios.put(`${API}/feedback/${item.id}/status`, payload);
      setItems(prev => prev.map(i => (i.id === item.id ? res.data : i)));
      toast.success(`Marked as ${STATUS_META[status]?.label || status}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  };

  const saveNote = async (item) => {
    const note = (noteEditing[item.id] ?? '').trim();
    await updateStatus(item, item.status, note);
    setNoteEditing(prev => { const n = { ...prev }; delete n[item.id]; return n; });
  };

  const sendReply = async (item) => {
    const message = (replyEditing[item.id] ?? '').trim();
    if (!message) {
      toast.error('Write a reply first');
      return;
    }
    setReplySending(item.id);
    try {
      const res = await axios.post(`${API}/feedback/${item.id}/reply`, { message });
      setItems(prev => prev.map(i => (i.id === item.id ? res.data : i)));
      setReplyEditing(prev => { const n = { ...prev }; delete n[item.id]; return n; });
      toast.success(`Reply emailed to ${item.author_email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send the reply');
    } finally {
      setReplySending(null);
    }
  };

  const visible = filter === 'all' ? items : items.filter(i => i.status === filter);
  const newCount = items.filter(i => i.status === 'new').length;

  if (loaded && items.length === 0) return null;

  return (
    <Card className="dashboard-card" data-testid="feedback-admin-panel">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3 pb-4">
        <CardTitle className="font-['Manrope'] text-xl font-bold text-[#1A1A1A] flex items-center gap-2">
          <MessageSquarePlus className="w-5 h-5 text-[#34657f]" />
          User Feedback
          {newCount > 0 && <Badge className="bg-orange-100 text-orange-800">{newCount} new</Badge>}
        </CardTitle>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40" aria-label="Filter feedback by status" data-testid="feedback-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.entries(STATUS_META).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent className="space-y-4">
        {visible.length === 0 && (
          <p className="text-sm text-[#5C5C5C] py-4 text-center">No feedback with this status.</p>
        )}
        {visible.map((item) => {
          const TypeIcon = item.type === 'problem' ? Bug : Lightbulb;
          const meta = STATUS_META[item.status] || STATUS_META.new;
          return (
            <div key={item.id} className="rounded-xl border border-slate-200 p-4 bg-[#F8F9FA]" data-testid={`feedback-item-${item.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={item.type === 'problem' ? 'bg-red-100 text-red-800' : 'bg-[#34657f]/10 text-[#34657f]'}>
                    <TypeIcon className="w-3 h-3 mr-1" />
                    {item.type === 'problem' ? 'Problem' : 'Suggestion'}
                  </Badge>
                  <Badge className={meta.cls} data-testid={`feedback-status-badge-${item.id}`}>{meta.label}</Badge>
                  <span className="text-xs text-[#5C5C5C]">
                    {item.author_name} ({item.author_email}) · {new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>
                <Select value={item.status} onValueChange={(v) => updateStatus(item, v)}>
                  <SelectTrigger className="w-36 h-8 text-xs bg-white" aria-label="Set feedback status" data-testid={`feedback-status-select-${item.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_META).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-sm text-[#1A1A1A] mt-3 whitespace-pre-wrap break-words">{item.message}</p>
              {item.screenshot_url && (
                <a
                  href={mediaSrc(item.screenshot_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 mt-2 text-xs font-semibold text-[#34657f] hover:text-[#b34e00] underline underline-offset-2"
                  data-testid={`feedback-screenshot-link-${item.id}`}
                >
                  <ImageIcon className="w-3.5 h-3.5" />
                  View screenshot
                </a>
              )}
              <div className="mt-3">
                {(item.replies || []).length > 0 && (
                  <div className="space-y-1.5 mb-2">
                    {item.replies.map((r, ri) => (
                      <p key={ri} className="text-xs text-[#3d7a22] bg-[#f0f7ec] rounded-md px-3 py-2" data-testid={`feedback-reply-${item.id}-${ri}`}>
                        <Reply className="w-3 h-3 inline mr-1" />
                        <span className="font-semibold">{r.sent_by}</span> replied on {new Date(r.sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}: {r.message}
                      </p>
                    ))}
                  </div>
                )}
                {replyEditing[item.id] !== undefined ? (
                  <div className="space-y-2 mb-2">
                    <Textarea
                      rows={2}
                      className="text-sm bg-white"
                      placeholder={`Short reply — emailed to ${item.author_email}…`}
                      value={replyEditing[item.id]}
                      onChange={(e) => setReplyEditing(prev => ({ ...prev, [item.id]: e.target.value }))}
                      maxLength={2000}
                      data-testid={`feedback-reply-input-${item.id}`}
                    />
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" size="sm" onClick={() => setReplyEditing(prev => { const n = { ...prev }; delete n[item.id]; return n; })}>Cancel</Button>
                      <Button size="sm" className="bg-[#3d7a22] hover:bg-[#2f6119]" disabled={replySending === item.id} onClick={() => sendReply(item)} data-testid={`feedback-reply-send-${item.id}`}>
                        <Reply className="w-3.5 h-3.5 mr-1.5" />
                        {replySending === item.id ? 'Sending…' : 'Send reply'}
                      </Button>
                    </div>
                  </div>
                ) : null}
                {noteEditing[item.id] !== undefined ? (
                  <div className="space-y-2">
                    <Textarea
                      rows={2}
                      className="text-sm bg-white"
                      placeholder="Internal note (e.g., why declined, target release)…"
                      value={noteEditing[item.id]}
                      onChange={(e) => setNoteEditing(prev => ({ ...prev, [item.id]: e.target.value }))}
                      data-testid={`feedback-note-input-${item.id}`}
                    />
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" size="sm" onClick={() => setNoteEditing(prev => { const n = { ...prev }; delete n[item.id]; return n; })}>Cancel</Button>
                      <Button size="sm" className="bg-[#34657f] hover:bg-[#2a5060]" onClick={() => saveNote(item)} data-testid={`feedback-note-save-${item.id}`}>Save note</Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-2">
                    {item.admin_note && (
                      <p className="text-xs text-[#5C5C5C] italic flex-1" data-testid={`feedback-note-${item.id}`}>
                        <StickyNote className="w-3 h-3 inline mr-1 text-[#b34e00]" />
                        {item.admin_note}
                      </p>
                    )}
                    <div className="ml-auto shrink-0 flex items-center gap-3">
                      {replyEditing[item.id] === undefined && (
                        <button
                          type="button"
                          onClick={() => setReplyEditing(prev => ({ ...prev, [item.id]: '' }))}
                          className="text-xs font-semibold text-[#3d7a22] hover:text-[#2f6119] underline underline-offset-2"
                          data-testid={`feedback-reply-btn-${item.id}`}
                        >
                          {(item.replies || []).length > 0 ? 'Reply again' : 'Reply by email'}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setNoteEditing(prev => ({ ...prev, [item.id]: item.admin_note || '' }))}
                        className="text-xs font-semibold text-[#34657f] hover:text-[#b34e00] underline underline-offset-2"
                        data-testid={`feedback-add-note-${item.id}`}
                      >
                        {item.admin_note ? 'Edit note' : 'Add note'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default FeedbackAdminPanel;
